import React, { useState, useEffect, useRef, useMemo } from "react";
import { tw } from "@careinfo/tailwind";
import { Controller } from "react-hook-form";
import debounce from "lodash.debounce";
import useGetHook from "../../../customHooks/useGetHook";
import APIS from "../../../constant/Endpoint";

const isValidPhone = (phone) => /^[+0-9]{7,15}$/.test(phone.trim());

const MultiPhoneInput = ({ name, control, value, errors, label }) => {
  const [inputValue, setInputValue] = useState("");
  const [inlineError, setInlineError] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [contactPhones, setContactPhones] = useState([]);
  const containerRef = useRef(null);
  const debouncedSearch = useMemo(() => debounce(setSearch, 300), []);

  // Fetch phones based on search
  const { data } = useGetHook({
    queryKey: ["phone-contacts", `phones-${search}`],
    url: APIS.SEARCH,
    params: {
      search: search,
    },
  });

  useEffect(() => {
    if (Array.isArray(data)) {
      setContactPhones(data ?? []);
    }
  }, [data]);

  useEffect(() => {
    if (inputValue.trim()) {
      debouncedSearch(inputValue.trim());
    }
  }, [inputValue]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [inputValue, contactPhones]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredSuggestions = useMemo(() => {
    const suggestions = new Set();
    const result = [];
    let phoneMatchCount = 0;

    for (const contact of contactPhones) {
      if (phoneMatchCount >= 5) break;
      if (Array.isArray(contact.phones)) {
        const matches = contact.phones.filter(
          (p) =>
            p.phone &&
            p.phone.toLowerCase().includes(inputValue.toLowerCase()) &&
            !suggestions.has(p.phone) &&
            !value.includes(p.phone)
        );

        const groupNameMatches = contact.name
          ?.toLowerCase()
          .includes(inputValue.toLowerCase());

        const groupHasUnselectedPhone = contact.phones.some(
          (p) => p.phone && !value.includes(p.phone)
        );
        if (matches.length > 0 || groupNameMatches) {
          matches.forEach((p) => suggestions.add(p.phone));
          phoneMatchCount += matches.length;
          result.push({ ...contact, matchedPhones: matches });
        }
      } else if (
        contact.phone &&
        contact.phone.toLowerCase().includes(inputValue.toLowerCase()) &&
        !suggestions.has(contact.phone) &&
        !value.includes(contact.phone)
      ) {
        suggestions.add(contact.phone);
        phoneMatchCount += 1;
        result.push(contact);
      }
    }

    return result;
  }, [contactPhones, inputValue, value]);

  const addPhone = (phoneOrContact, value, onChange) => {
    const newNumbers = [];

    if (typeof phoneOrContact === "string") {
      if (!isValidPhone(phoneOrContact)) {
        setInlineError("Invalid phone number format");
        return;
      }
      if (!value.includes(phoneOrContact)) {
        newNumbers.push(phoneOrContact);
      }
    } else if (phoneOrContact?.phone) {
      if (
        isValidPhone(phoneOrContact.phone) &&
        !value.includes(phoneOrContact.phone)
      ) {
        newNumbers.push(phoneOrContact.phone);
      }
    } else if (Array.isArray(phoneOrContact?.phones)) {
      phoneOrContact.phones.forEach((p) => {
        if (isValidPhone(p.phone) && !value.includes(p.phone)) {
          newNumbers.push(p.phone);
        }
      });
    }

    if (newNumbers.length > 0) {
      onChange([...value, ...newNumbers]);
      setInputValue("");
      setInlineError("");
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e, value, onChange) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (
        highlightedIndex >= 0 &&
        highlightedIndex < filteredSuggestions.length
      ) {
        addPhone(filteredSuggestions[highlightedIndex], value, onChange);
      } else if (inputValue.trim()) {
        addPhone(inputValue.trim(), value, onChange);
      }
    } else if (e.key === ",") {
      e.preventDefault();
      if (inputValue.trim()) {
        addPhone(inputValue.trim(), value, onChange);
      }
    }
  };

  const handleRemove = (index, value, onChange) => {
    const updated = value.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className={tw`relative`} ref={containerRef}>
      {label && <label>{label}</label>}
      <Controller
        name={name}
        control={control}
        defaultValue={[]}
        render={({ field }) => (
          <>
            <div
              className={tw`flex flex-wrap mt-1 gap-2 p-2 bg-[#EEF5F4] rounded-md`}
            >
              {field.value.map((phone, index) => (
                <div
                  key={index}
                  className={tw`bg-blue-100 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1`}
                >
                  <span>{phone}</span>
                  <button
                    type="button"
                    className={tw`ml-1 text-red-600 hover:text-red-800 focus:outline-none`}
                    onClick={() =>
                      handleRemove(index, field.value, field.onChange)
                    }
                  >
                    &times;
                  </button>
                </div>
              ))}
              <input
                type="text"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setShowSuggestions(true);
                  if (inlineError) setInlineError("");
                }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={(e) => handleKeyDown(e, field.value, field.onChange)}
                className={tw`bg-transparent outline-none flex-1`}
                placeholder="Type number and press Enter"
              />
            </div>

            {showSuggestions && filteredSuggestions.length > 0 && (
              <ul
                className={tw`absolute z-10 bg-white border rounded shadow w-full mt-1 max-h-48 overflow-auto`}
              >
                {filteredSuggestions.map((contact, index) => {
                  const isHighlighted = index === highlightedIndex;
                  const isGroup = Array.isArray(contact.phones);
                  const display = isGroup
                    ? `${contact.name}`
                    : `${contact.phone}, ${contact.name}`;
                  return (
                    <li
                      key={index}
                      className={tw`px-3 py-2 cursor-pointer text-sm ${
                        isHighlighted ? "bg-gray-200" : "hover:bg-gray-100"
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        addPhone(contact, field.value, field.onChange);
                      }}
                    >
                      {display}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      />
      {(inlineError || errors) && (
        <p className={tw`text-red-500 text-xs mt-1`}>
          {inlineError || errors?.message}
        </p>
      )}
    </div>
  );
};

export default MultiPhoneInput;
